// Unified 平日/祝日/会社休業日 helper.
//
// Combines the offline-first algorithmic JP holiday library
// (`src/lib/jpHolidays.js`) with company-specific holidays + weekend
// settings fetched from `/api/holidays`. Results are cached per year in
// module scope so pages that mount several date pickers only make ONE
// backend request per year.
//
// Usage inside a component:
//   const { isNonWorking, nonWorkingReason, nextWorkingDay, weekend, loading }
//     = useHolidays(2026);
//
//   isNonWorking('2026-05-05')      → true
//   nonWorkingReason('2026-05-05')  → { reason:'holiday', name:'こどもの日' }
//   nonWorkingReason('2026-02-14')  → { reason:'weekend', name:'土曜' }
//   nextWorkingDay('2026-05-03')    → '2026-05-07'
//
// The hook is idempotent — mounting it multiple times in the same tree
// still only fetches once per year.

import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { holidayName, isHoliday } from '@/lib/jpHolidays';

const API_ROOT = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

// Module-level cache — {year: {items: […], weekend: {…}, fetched_at}}
const _cache = new Map();
const _inflight = new Map();

async function fetchYear(year) {
  const key = String(year);
  if (_cache.has(key)) return _cache.get(key);
  if (_inflight.has(key)) return _inflight.get(key);
  const p = axios
    .get(`${API_ROOT}/holidays`, { params: { year } })
    .then((r) => {
      _cache.set(key, r.data);
      _inflight.delete(key);
      return r.data;
    })
    .catch((e) => {
      _inflight.delete(key);
      // Fall back to offline data — algorithmic JP holidays library only
      const fallback = { year, items: [], weekend: { saturday_off: true, sunday_off: true } };
      _cache.set(key, fallback);
      return fallback;
    });
  _inflight.set(key, p);
  return p;
}

/** Invalidate the module cache so the next hook mount re-fetches. */
export function invalidateHolidayCache(year) {
  if (year == null) {
    _cache.clear();
    return;
  }
  _cache.delete(String(year));
}

const _isoDate = (dt) => {
  if (!dt) return '';
  if (typeof dt === 'string') return dt.slice(0, 10);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const _parse = (v) => {
  if (!v) return null;
  if (v instanceof Date) return isNaN(v.getTime()) ? null : v;
  const [y, m, d] = String(v).slice(0, 10).split('-').map(Number);
  if (!y || !m || !d) return null;
  const dt = new Date(y, m - 1, d);
  return isNaN(dt.getTime()) ? null : dt;
};

export function useHolidays(year) {
  const targetYear = year || new Date().getFullYear();
  const [data, setData] = useState(() => _cache.get(String(targetYear)) || null);
  const [loading, setLoading] = useState(() => !_cache.has(String(targetYear)));

  useEffect(() => {
    let alive = true;
    if (_cache.has(String(targetYear))) {
      setData(_cache.get(String(targetYear)));
      setLoading(false);
      return () => {};
    }
    setLoading(true);
    fetchYear(targetYear).then((d) => {
      if (!alive) return;
      setData(d);
      setLoading(false);
    });
    return () => { alive = false; };
  }, [targetYear]);

  const api = useMemo(() => {
    const items = data?.items || [];
    const weekend = data?.weekend || { saturday_off: true, sunday_off: true };
    // Build maps for O(1) lookup
    const officialMap = new Map();
    const companyMap = new Map();
    const overriddenSet = new Set();
    items.forEach((it) => {
      if (it.kind === 'official') {
        officialMap.set(it.date, it);
        if (it.overridden) overriddenSet.add(it.date);
      } else if (it.kind === 'company') {
        companyMap.set(it.date, it);
      }
    });

    const nonWorkingReason = (v) => {
      const dt = _parse(v);
      if (!dt) return null;
      const iso = _isoDate(dt);
      // Same-year fast path uses the fetched data; other years use offline lib
      if (dt.getFullYear() === targetYear) {
        // Company wins over official
        const comp = companyMap.get(iso);
        if (comp) return { reason: 'company', name: comp.name, note: comp.note };
        const off = officialMap.get(iso);
        if (off && !overriddenSet.has(iso)) return { reason: 'holiday', name: off.name };
      } else {
        // Cross-year lookup falls back to algorithmic
        const off = holidayName(dt);
        if (off) return { reason: 'holiday', name: off };
      }
      const dow = dt.getDay(); // 0=Sun..6=Sat
      if (dow === 6 && weekend.saturday_off) return { reason: 'weekend', name: '土曜' };
      if (dow === 0 && weekend.sunday_off) return { reason: 'weekend', name: '日曜' };
      return null;
    };

    const isNonWorking = (v) => !!nonWorkingReason(v);

    const nextWorkingDay = (v, direction = 'forward', maxIter = 60) => {
      const dt = _parse(v);
      if (!dt) return '';
      const step = direction === 'forward' ? 1 : -1;
      const cur = new Date(dt.getTime());
      for (let i = 0; i < maxIter; i++) {
        if (!isNonWorking(cur)) return _isoDate(cur);
        cur.setDate(cur.getDate() + step);
      }
      return _isoDate(cur);
    };

    // Count working days between (inclusive)
    const workingDaysBetween = (a, b) => {
      const s = _parse(a);
      const e = _parse(b);
      if (!s || !e) return 0;
      const [start, end] = s.getTime() <= e.getTime() ? [s, e] : [e, s];
      const cur = new Date(start.getTime());
      let count = 0;
      while (cur.getTime() <= end.getTime()) {
        if (!isNonWorking(cur)) count++;
        cur.setDate(cur.getDate() + 1);
      }
      return count;
    };

    return {
      loading,
      year: targetYear,
      weekend,
      items,
      isNonWorking,
      nonWorkingReason,
      nextWorkingDay,
      workingDaysBetween,
      isHoliday: (v) => {
        const dt = _parse(v);
        if (!dt) return false;
        const iso = _isoDate(dt);
        if (dt.getFullYear() === targetYear) {
          if (companyMap.has(iso)) return true;
          const off = officialMap.get(iso);
          return !!off && !overriddenSet.has(iso);
        }
        return isHoliday(dt);
      },
      isWeekend: (v) => {
        const dt = _parse(v);
        if (!dt) return false;
        const dow = dt.getDay();
        if (dow === 6 && weekend.saturday_off) return true;
        if (dow === 0 && weekend.sunday_off) return true;
        return false;
      },
      holidayName: (v) => {
        const dt = _parse(v);
        if (!dt) return null;
        const iso = _isoDate(dt);
        if (dt.getFullYear() === targetYear) {
          const comp = companyMap.get(iso);
          if (comp) return comp.name;
          const off = officialMap.get(iso);
          return off && !overriddenSet.has(iso) ? off.name : null;
        }
        return holidayName(dt);
      },
    };
  }, [data, targetYear, loading]);

  return api;
}
